/**
 * PDFViewerPanel - Panel para visualizar PDFs
 * Usa PDFViewerModal en modo embedded
 * Con sincronización bidireccional con ContentPanel
 *
 * REFACTORED: Now uses extracted hooks and UI components
 */

import React from 'react';
import type { PanelTabType } from './PanelToolSelector';
import { useTheme } from '../contexts/ThemeContext';
import '../styles/pdf-viewer.css';
import apiService from '../services/api';
import {
  usePDFViewerPanelState,
  usePDFViewerPanelMetadata,
  usePDFViewerPanelCopy,
  PDFViewerPanelPlaceholder,
  PDFViewerPanelSavedMessageView,
  PDFViewerPanelSnippetView,
  PDFViewerPanelPDFView,
  PDFViewerPanelNoPDFView,
} from './pdf-panel';
import MarkdownRenderer from './MarkdownRenderer';
import type { PDFViewerPanelProps as PDFViewerPanelPropsType } from './pdf-panel/types/pdf-viewer-panel.types';
import type { CitationData, UsageStatsData, RagStatsData } from './pdf-panel/types/pdf-viewer-panel.types';

interface PDFViewerPanelProps extends Omit<PDFViewerPanelPropsType, 'citationToView'> {
  citationToView: PDFViewerPanelPropsType['citationToView'];
}

const PDFViewerPanel: React.FC<PDFViewerPanelProps> = ({
  citationToView,
  onCitationClick,
  onChunkClick,
  onClose,
  hideHeader = false,
  onSnippetSaved,
}) => {
  const { theme } = useTheme();
  const currentUser = apiService.getCurrentUser();
  const isAdmin = currentUser?.is_admin || false;

  // Hooks extraídos
  const {
    diagnosis,
    showDiagnosis,
    setShowDiagnosis,
    pdfError,
    setPdfError,
    currentPage,
    referencesExpanded,
    setReferencesExpanded,
    handlePageChange,
  } = usePDFViewerPanelState({
    citationToView,
  });

  const {
    savedMessageMetadata,
    setSavedMessageMetadata,
    resolvedDocumentId,
    resolvedProjectId,
    hasDocument,
    isStudioResult,
    isSnippet,
    isSavedMessage,
    detailMetadata,
    detailTitle,
    detailText,
    savedMessageMarkdown,
    snippetMarkdown,
    studioMarkdown,
  } = usePDFViewerPanelMetadata({
    citationToView,
  });

  const { handleCopy } = usePDFViewerPanelCopy({
    isSavedMessage,
    isStudioResult,
    isSnippet,
    savedMessageMarkdown,
    studioMarkdown,
    snippetMarkdown,
    detailText,
  });

  // Early return si no hay citationToView
  if (!citationToView) {
    return (
      <PDFViewerPanelPlaceholder
        hideHeader={hideHeader}
        diagnosis={diagnosis}
        showDiagnosis={showDiagnosis}
        setShowDiagnosis={setShowDiagnosis}
        onClose={onClose}
        theme={theme}
      />
    );
  }

  // Extraer datos adicionales del metadata
  const notes = typeof detailMetadata?.notes === 'string' ? detailMetadata.notes : undefined;
  const tags = Array.isArray(detailMetadata?.tags) ? detailMetadata.tags : undefined;
  const detailType = typeof detailMetadata?.type === 'string' ? detailMetadata.type : undefined;
  const sourcesCount = typeof detailMetadata?.sources_count === 'number' ? detailMetadata.sources_count : undefined;
  const createdAt = detailMetadata?.created_at ? new Date(detailMetadata.created_at) : undefined;
  const updatedAt = detailMetadata?.updated_at ? new Date(detailMetadata.updated_at) : undefined;
  const usageStats = detailMetadata?.usage_stats ? (detailMetadata.usage_stats as UsageStatsData) : null;
  const ragStats = detailMetadata?.rag_stats ? (detailMetadata.rag_stats as RagStatsData) : null;
  const savedMessageCitations: Record<string, CitationData> = (detailMetadata?.citations as Record<string, CitationData>) || {};

  // Renderizar vista según tipo y si hay documento
  if (isStudioResult && !hasDocument) {
    return (
      <div className={`flex flex-col h-full ${theme.colors.bg.primary}`}>
        <div className="flex-1 overflow-auto p-4">
          {detailTitle && (
            <h2 className={`text-lg font-semibold mb-4 ${theme.colors.text.primary}`}>
              {detailTitle}
            </h2>
          )}
          <MarkdownRenderer content={studioMarkdown || ''} />
        </div>
      </div>
    );
  }

  if (isSavedMessage && !hasDocument) {
    return (
      <PDFViewerPanelSavedMessageView
        savedMessageMarkdown={savedMessageMarkdown}
        detailTitle={detailTitle}
        detailText={detailText}
        detailMetadata={detailMetadata}
        savedMessageMetadata={savedMessageMetadata}
        setSavedMessageMetadata={setSavedMessageMetadata}
        onCitationClick={onCitationClick}
        onChunkClick={onChunkClick}
        referencesExpanded={referencesExpanded}
        setReferencesExpanded={setReferencesExpanded}
        usageStats={usageStats}
        ragStats={ragStats}
        isAdmin={isAdmin}
        onCopy={handleCopy}
        theme={theme}
      />
    );
  }

  if (isSnippet && !hasDocument) {
    return (
      <PDFViewerPanelSnippetView
        snippetMarkdown={snippetMarkdown}
        detailTitle={detailTitle}
        detailText={detailText}
        detailMetadata={detailMetadata}
        onCopy={handleCopy}
        theme={theme}
      />
    );
  }

  // Vista con PDF (embedded)
  if (hasDocument) {
    return (
      <PDFViewerPanelPDFView
        citationToView={citationToView}
        hideHeader={hideHeader}
        resolvedDocumentId={resolvedDocumentId}
        resolvedProjectId={resolvedProjectId}
        currentPage={currentPage}
        pdfError={pdfError}
        setPdfError={setPdfError}
        diagnosis={diagnosis}
        showDiagnosis={showDiagnosis}
        setShowDiagnosis={setShowDiagnosis}
        handlePageChange={handlePageChange}
        detailTitle={detailTitle}
        detailText={detailText}
        notes={notes}
        tags={tags}
        detailType={detailType}
        sourcesCount={sourcesCount}
        createdAt={createdAt}
        updatedAt={updatedAt}
        isSavedMessage={isSavedMessage}
        isStudioResult={isStudioResult}
        savedMessageCitations={savedMessageCitations}
        usageStats={usageStats}
        ragStats={ragStats}
        isAdmin={isAdmin}
        onClose={onClose}
        onSnippetSaved={onSnippetSaved}
        onCitationClick={onCitationClick}
        onChunkClick={onChunkClick}
        handleCopy={handleCopy}
        theme={theme}
      />
    );
  }

  // Vista sin PDF cuando hay contenido pero no documento
  return <PDFViewerPanelNoPDFView theme={theme} />;
};

export default PDFViewerPanel;
